<template>
  <div>
    <component
      data-cy="active-module"
      :is="componentType"
      :name="componentType"
      :applicationModule="applicationModule"
      :submitSections="submitSections"
      :isBackEvent="isBackEvent"
      :showBackButton="!!(slugPrevious || configUrlPrevious)"
      :showNextButton="!!(slugNext || configNext)"
      :helpDrawer="helpDrawer"
      :skipping="skipping"
      @refresh="refresh"
      @next="next"
      @back="back"
      @skipModule="skipModule"
      @trackPage="trackPage"
      @postEvent="postEvent"
    >
      <template #skipMessage v-if="applicationModule?.showSkip && applicationModule?.type !== 'SUBMIT'">
        <p class="pt-5 pb-0 my-0" style="max-width:450px">
          <span>{{ skipModuleMessage.start }}</span>
          <br/>
          <span>
            <button class="btn btn-link align-baseline text-size-inherit" @click="skipModule">
              {{ skipModuleMessage.link }}
            </button>
            {{ skipModuleMessage.end }}
          </span>
        </p>
      </template>
    </component>
  </div>
</template>

<script>
import { defineAsyncComponent, computed } from 'vue'

import { getHelpDrawer } from '~/libs/drawer'

import { storeToRefs } from 'pinia'
import { useApplicationConfigStore } from '~/store/application-config'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useDarkLaunchStore } from '~/store/dark-launch'
import { useBorrowerStore } from '~/store/borrower'
import { useProgressStore } from '~/store/progress'
import { useOptInsStore } from '~/store/opt-ins'
import { useRootStore } from '~/store/root'
import { useAppAnalyticsStore } from '~/store/app-analytics'

/**
 * AppModule
 *
 * Responsibilities:
 * - Render applicationModule for current slug.
 * - Determine which vue component to render for the ApplicationModule.type.
 * - Routing between modules.
 */
export default {
  name: 'AppModule',
  // Mapping the dynamic loading component to the appModuleType option
  // TODO: Revisit to fix unit testing and auto import with 'Lazy' prefix
  components: {
    APPLICATION_FIELDS: defineAsyncComponent(() => import('~/components/app-config/app-modules/ApplicationFieldsAppModule.vue')),
    CREDIT_CHECK: defineAsyncComponent(() => import('~/components/app-config/app-modules/CreditCheckAppModule.vue')),
    BANK_STATEMENTS: defineAsyncComponent(() => import('~/components/app-config/app-modules/BankStatementsAppModule.vue')),
    SUBMIT: defineAsyncComponent(() => import('~/components/app-config/app-modules/SubmitDynamicModule.vue')),
    DOCUMENTS: defineAsyncComponent(() => import('~/components/app-config/app-modules/DocumentsAppModule.vue')),
    DEBT_SCHEDULE_UPLOAD: defineAsyncComponent(() => import('~/components/app-config/app-modules/DebtScheduleAppModule.vue')),
    ADDITIONAL_OWNERS: defineAsyncComponent(() => import('~/components/app-config/app-modules/AdditionalOwnersAppModule.vue')),
    ADDITIONAL_OWNERS_TAXES: defineAsyncComponent(() => import('~/components/app-config/app-modules/AdditionalOwnersTaxesAppModule.vue')),
    UNKNOWN: defineAsyncComponent(() => import('~/components/app-config/app-modules/UnknownAppModule.vue'))
  },
  props: {
    applicationModule: {
      type: Object,
      required: true
    },
    submitSections: {
      type: Object,
      required: false,
    },
    moduleUrlPrefix: {
      type: String,
      required: true
    },
    configPrevious: {
      type: String,
      default: ''
    },
    configNext: {
      type: String,
      default: ''
    },
    configSkip: {
      type: String,
      default: ''
    },
    showConfigSkip: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    // setup stores and composables
    function getComponentType (slug) {
      switch (props.applicationModule?.type) {
        case 'ADDITIONAL_OWNERS':
        case 'APPLICATION_FIELDS':
        case 'BANK_STATEMENTS':
        case 'CREDIT_CHECK':
        case 'DOCUMENTS':
        case 'ADDITIONAL_OWNERS_TAXES':
        case 'SUBMIT':
          return props.applicationModule?.type
        case 'CUSTOM':
          switch (slug) {
            case 'debt-schedule-upload':
              return 'DEBT_SCHEDULE_UPLOAD'
            default:
            return 'UNKNOWN'
          }
        default:
          return 'UNKNOWN'
      }
    }
    const { $lendioCookies } = useNuxtApp()
    const $config = useRuntimeConfig().public
    const slugPrevious = props.applicationModule?.navigationSlugs?.back ?? null
    const slugNext = props.applicationModule?.navigationSlugs?.next ?? null
    const appAnalyticsStore = useAppAnalyticsStore()
    const { fsTrackEvent, fauxBorrowerPageTrackingEvent } = appAnalyticsStore
    const applicationConfigStore = useApplicationConfigStore()
    const affiliateCustomizationStore = useAffiliateCustomizationStore()
    const optInsStore = useOptInsStore()
    const darkLaunchStore = useDarkLaunchStore()
    const borrowerStore = useBorrowerStore()
    const { getByIdentifierAndSlug, isBackEvent} = applicationConfigStore
    const { applicationConfig } = storeToRefs(applicationConfigStore)
    const { updateApplication } = useProgressStore()
    const route = useRoute()

    const configUrlPrevious =  props.configPrevious

    const { ref: referrer, franchiseId } = route.query
    if (!referrer && franchiseId) {
      route.query.ref = franchiseId
    }

    // Set tracking cookies like ref / utm_*
    $lendioCookies.setCookiesFromParams(route.query, {
      secure: $config.environment !== 'dev'
    })

    // define key variables
    const { identifier, slug } = getAppConfigRouteData()

    const skipModuleMessage = {
      start: 'Not ready to provide the required documentation?',
      link: 'Submit application now, and upload documents later.',
      end: ''
    }

    // Override skipModuleMessage for specific configs
    switch (identifier) {
      case 'sba':
      case 'emsba':
      case 'ltemsba':
        skipModuleMessage.start = 'Not ready to upload this required SBA loan document?'
        skipModuleMessage.link = 'Continue to the next document'
        skipModuleMessage.end = 'and provide this one later. Not providing all required documents will affect your eligibility to receive SBA loan options.'
        break
      case 'equipment':
        skipModuleMessage.start = 'Not ready to upload this required Equipment Loan documentation?'
        skipModuleMessage.link = 'Continue and upload Equipment documents later'
        break
    }

    const skipping = ref(false)

    const componentType = getComponentType(slug)

    function postEvent(eventType) {
      if (!identifier || !slug || !eventType) {
        return
      }
      const dynamicModules = ['submit'];
      if (dynamicModules.includes(slug) && eventType.includes('module')) {
        // Swap 'module' in eventType string to dynamic module name; IE. 'moduleLoaded' -> 'submitLoaded'
        eventType = eventType.replace('module', slug)
      }

      applicationConfigStore.postApplicationEvent({ identifier, slug, eventType, path: useRoute().path })
      const fsProps = {
        name: 'Post Module Event',
        properties: {
          event: eventType,
          moduleType: componentType,
          applicationIdentifier: identifier,
          applicationSlug: slug,
        }
      }
      fsTrackEvent(fsProps)
    }

    // define HELOC props
    const HELOC_ALT_OPTION_ID = 30
    const qualifiedForHeloc = computed(() => {
      return optInsStore.allOptions?.some(offer => offer.id === HELOC_ALT_OPTION_ID) ?? false;
    })
    const isHelocTestActive = computed(() => {
      return darkLaunchStore?.darkFlags?.['helocTestActive'] ?? false
    })

    const helpDrawer = getHelpDrawer({ identifier, slug })

    const qualifiedForExpectationsStoryArc = computed(() => {
      return borrowerStore.isEligibleForExpectationsStoryArc && !borrowerStore.isViewedExpectationsStoryArc
    })

    return {
      identifier,
      slug,
      postEvent,
      componentType,
      helpDrawer,
      slugPrevious,
      slugNext,
      configUrlPrevious,
      applicationConfig,
      getByIdentifierAndSlug,
      updateApplication,
      isBackEvent,
      applicationConfigStore,
      affiliateCustomizationStore,
      qualifiedForHeloc,
      isHelocTestActive,
      skipModuleMessage,
      skipping,
      fauxBorrowerPageTrackingEvent,
      fsTrackEvent,
      qualifiedForExpectationsStoryArc,
    }
  },
  mounted() {
    const rootStore = useRootStore()
    this.postEvent('moduleLoaded')

    // Hack until we can clean up pwfSignupInProgress fully
    if (rootStore.pwfSignupInProgress) {
      rootStore.setPWFSignupStatus(false)
    }
  },
  computed: {},
  methods: {
    getProgression() {
      const routeProgressMap = {
        'el': {
          defaults: { type: 'embedded' },
          'owner-info': { progression: 'ownerInfo' },
          'business-info': { progression: 'businessInfo' },
          'credit-check': { progression: 'creditCheck' },
        },
        'marketplace': {
          defaults: { type: 'marketplace' },
        }
      }
      const identifierMap = routeProgressMap?.[this.identifier]
      const progressDefaults = identifierMap?.defaults
      const progress = {
        ... progressDefaults,
        ... identifierMap?.[this.slug]
      }

      return Object.keys(progress).length > 0 ? progress : null
    },
    async refresh() {
      await this.getByIdentifierAndSlug({ identifier: this.identifier, slug: this.slug })
    },
    async back() {
      // If its the first possible module, dont wait for module list load or attempt slug navigation
      if (!this.applicationModule?.isFirstPossibleModule) {
        // Prefetch for the next load
        await this.getByIdentifierAndSlug({ identifier: this.identifier, slug: this.slug, direction: 'back' })
        // try slug for previous applicationModule
        const slugUrl = this.buildSlugUrl(this.slugPrevious)
        if (slugUrl) {
          return navigateTo(slugUrl)
        }
      }

      // try applicationConfig.urlPrevious
      if (this.configUrlPrevious) {
        return navigateTo(`/${this.configUrlPrevious}`)
      }

      // error state
      log.error('AppModule@back called with nowhere to go')
    },
    async skipModule() {
      this.skipping = true
      this.postEvent('moduleSkipped')
      return this.navigateNext()
    },
    async navigateNext(progress) {
      // If its the last possible module, dont wait for module list load or attempt slug navigation
      if (!this.applicationModule?.isLastPossibleModule) {
        await Promise.allSettled([
          progress ? this.updateApplication(progress) : null,
          this.getByIdentifierAndSlug({ identifier: this.identifier, slug: this.slug, direction: 'forward' }) // Prefetch for the next load
        ])

        // get updated next navigation slug from applicationConfig store ref
        // - needed for when module list has updated. e.g. the next module has updated as a result of answers on current module
        // - may want to change next/back to be computed in the future
        const updatedNextSlug = this.applicationConfig?.modules.find(module => module.urlSlug === this.slug)?.navigationSlugs?.next || this.slugNext
        const slugUrl = this.buildSlugUrl(updatedNextSlug)

        if (this.identifier === 'marketplace' &&  this.slug === 'basic-info') {
          return navigateTo('/owner-info')
        }

        if (slugUrl) {
          return navigateTo(slugUrl)
        }
      }

      if (progress) {
        await this.updateApplication(progress)
      }

      let url = this.configNext

      // This is temporal check for HELOC option
      if (this.qualifiedForHeloc && url === 'portal' && this.isHelocTestActive) {
        url = 'heloc-option'
      }

      if (this.qualifiedForExpectationsStoryArc && url === 'portal') {
        url = 'next-steps/what-comes-next'
      }

      if (url === 'portal') {
        url = this.getPortalUrl()
      }

      if (url) {
        useBorrowerStore().getBorrower({ forceReload: true })
        return navigateTo(`/${url}`)
      }
    },
    async next() {
      const progress = this.getProgression()
      const navigated = this.navigateNext(progress)
      if (!navigated) {
        // error state
        log.error('AppModule@next called with nowhere to go')
      }
    },
    getPortalUrl () {
      let route = 'portal?completedApp=true'
      const isIntelligentLendingAffiliate = this.affiliateCustomizationStore.isIntelligentLendingAffiliate
      if (isIntelligentLendingAffiliate) {
        route = 'intelligent-lending/portal'
      }
      return route;
    },
    async trackPage(fsEventName, routeName = null) {
      this.fsTrackEvent({ name: fsEventName})

      if (!routeName) {
        return
      }

      this.fauxBorrowerPageTrackingEvent(`/${this.identifier}-app/${routeName}`)
    },
    buildSlugUrl(slug) {
      return slug ? `${this.moduleUrlPrefix}${this.identifier}-app/${slug}` : ''
    }
  }

}
</script>

<style lang="scss" scoped>
.text-size-inherit {
  font-size: inherit;
}
</style>
